import { get, writable } from "svelte/store";
import { language } from "../lang";
import { notifyWarning } from "./alert";

/*
    내장 기능(WygLore/MARP) 상태등.

    두 기능 모두 fail-open 설계라 서버 장애·설정 오류가 콘솔에만 남고
    사용자는 기억/보조분석이 조용히 꺼진 것을 알 수 없었다. 각 기능의
    실제 성공/실패 지점에서 reportFeatureHealth() 를 호출하면:
      - 빠른 설정 허브의 상태 점(초록/빨강/회색)이 실시간 반영되고
      - 정상→오류 전이 시 세션당 1회, 비차단 토스트로 알린다.
    fail-open 자체는 유지된다 — 상태 보고는 UX 병렬 관심사일 뿐 채팅을 막지 않는다.
*/

// unconfigured: 기능은 켜져 있으나 필수 설정(모델·API 키)이 비어 있는 상태.
// 장애(error)가 아니라 "설정 필요" 안내 대상이므로 적색·경고 토스트를 쓰지 않는다.
export type FeatureHealthLevel = "unknown" | "ok" | "error" | "off" | "unconfigured";
export type FeatureName = "wyglore" | "marp";

export interface FeatureHealth {
    level: FeatureHealthLevel;
    /** 원인 요약(개발자용 짧은 문자열). 상태 점 title 로만 노출. */
    detail?: string;
}

const initial: FeatureHealth = { level: "unknown" };

export const wygloreHealthStore = writable<FeatureHealth>(initial);
export const marpHealthStore = writable<FeatureHealth>(initial);

// 세션당 기능별 1회만 토스트 — 채팅마다 반복되는 실패로 알림이 도배되는 것을 막는다.
const toastedFeatures = new Set<FeatureName>();

function storeOf(feature: FeatureName) {
    return feature === "wyglore" ? wygloreHealthStore : marpHealthStore;
}

export function reportFeatureHealth(feature: FeatureName, level: FeatureHealthLevel, detail?: string): void {
    const store = storeOf(feature);
    const prev = get(store);
    if (prev.level === level && prev.detail === detail) return;
    // "설정 필요"가 확정된 상태에서 오는 error 는 거의 확실히 그 미설정이 원인이다
    // (예: 빈 키로 호출 → 401). 더 구체적인 unconfigured 를 유지해 오탐 적색을 막는다.
    if (level === "error" && prev.level === "unconfigured") return;
    store.set({ level, detail });

    if (level === "error" && prev.level !== "error" && !toastedFeatures.has(feature)) {
        toastedFeatures.add(feature);
        const msg = feature === "wyglore"
            ? language.featureHealthWygloreDown
            : language.featureHealthMarpDown;
        notifyWarning(msg, { description: detail, source: `${feature}-health` });
    }
}

export function getFeatureHealth(feature: FeatureName): FeatureHealth {
    return get(storeOf(feature));
}
